import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  fetchConsultationPatients,
  fetchJson,
  fetchLatestUserReport,
  getResolvedUserNo,
} from '../utils/agentUiApi';
import './UserTimelineConsolePage.css';

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  try {
    return new Date(value).toLocaleString('ko-KR');
  } catch {
    return String(value);
  }
}

function typeLabel(type) {
  switch (type) {
    case 'report':
      return '리포트';
    case 'conversation':
      return '상담';
    case 'autobio':
      return '서사';
    default:
      return type || 'unknown';
  }
}

function getPatientOptionId(patient) {
  const raw = patient?.std_patient_id ?? patient?.patient_id ?? patient?.id;
  return raw === null || raw === undefined ? '' : String(raw);
}

function formatPatientOptionLabel(patient) {
  const id = getPatientOptionId(patient);
  const rawName = patient?.name || patient?.patient_name || patient?.display_name || patient?.label || '';
  const name = String(rawName || '').trim() || `환자 ${id}`;
  return `가족 ${id} / ${name}`;
}

function UserTimelineConsolePage() {
  const params = useParams();
  const defaultUserNo = params.userNo || getResolvedUserNo() || '100000001';

  const [userNo, setUserNo] = useState(defaultUserNo);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [reportId, setReportId] = useState('');
  const [query, setQuery] = useState('혈압');
  const [timeline, setTimeline] = useState([]);
  const [memoryTurns, setMemoryTurns] = useState([]);
  const [memorySearch, setMemorySearch] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeError, setScopeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const patientOptions = useMemo(() => {
    const normalized = patients
      .map((patient) => ({
        id: getPatientOptionId(patient),
        label: formatPatientOptionLabel(patient),
      }))
      .filter((patient) => patient.id);

    if (patientId && !normalized.some((patient) => patient.id === patientId)) {
      return [
        { id: patientId, label: `가족 ${patientId} / 조회 기준 환자` },
        ...normalized,
      ];
    }

    return normalized;
  }, [patientId, patients]);

  const clearConsoleResults = () => {
    setTimeline([]);
    setMemoryTurns([]);
    setMemorySearch(null);
  };

  const loadIdentityConsole = async (overrides = {}) => {
    const effectiveUserNo = String(overrides.userNo ?? userNo ?? '').trim();
    const effectivePatientId = String(overrides.patientId ?? patientId ?? '').trim();
    const effectiveReportId = String(overrides.reportId ?? reportId ?? '').trim();

    if (!effectiveUserNo || !effectivePatientId) {
      setError('로그인 사용자(user_no)와 가족(patient_id)을 선택해 주세요.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const timelineQuery = new URLSearchParams({
        patient_id: String(effectivePatientId),
        page: '1',
        page_size: '20',
        order: 'desc',
      });
      const memoryTurnsQuery = new URLSearchParams({
        patient_id: String(effectivePatientId),
        page: '1',
        page_size: '10',
      });
      const memoryIndexQuery = new URLSearchParams({
        patient_id: String(effectivePatientId),
        limit: '5',
      });
      if (effectiveReportId) {
        memoryIndexQuery.set('report_id', effectiveReportId);
      }
      if (query.trim()) {
        memoryIndexQuery.set('query', query.trim());
      }

      const [timelineResponse, memoryTurnsResponse, memorySearchResponse] = await Promise.all([
        fetchJson(`/api/v1/identity/${effectiveUserNo}/timeline?${timelineQuery.toString()}`),
        fetchJson(`/api/v1/identity/${effectiveUserNo}/memory-turns?${memoryTurnsQuery.toString()}`),
        fetchJson(`/api/v1/identity/${effectiveUserNo}/memory-index/search?${memoryIndexQuery.toString()}`),
      ]);

      setTimeline(Array.isArray(timelineResponse.items) ? timelineResponse.items : []);
      setMemoryTurns(Array.isArray(memoryTurnsResponse.data) ? memoryTurnsResponse.data : []);
      setMemorySearch(memorySearchResponse);
    } catch (loadError) {
      setError(loadError.message || 'Identity Console 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const initializePatientScope = async (effectiveUserNo, { autoLoad = true } = {}) => {
    const normalizedUserNo = String(effectiveUserNo || '').trim();
    if (!normalizedUserNo) {
      setScopeError('로그인 사용자(user_no)를 입력해 주세요.');
      return;
    }

    setScopeLoading(true);
    setScopeError('');
    setError('');
    clearConsoleResults();
    try {
      const [patientList, latestReport] = await Promise.all([
        fetchConsultationPatients(normalizedUserNo),
        fetchLatestUserReport(normalizedUserNo),
      ]);
      const normalizedPatients = Array.isArray(patientList) ? patientList : [];
      const latestPatientId = latestReport?.patient_id ? String(latestReport.patient_id) : '';
      const defaultPatient = normalizedPatients.find((patient) => patient?.is_default) || normalizedPatients[0];
      const nextPatientId = latestPatientId || getPatientOptionId(defaultPatient);
      let nextReport = latestReport;

      if (nextPatientId && (!nextReport || String(nextReport.patient_id || '') !== nextPatientId)) {
        nextReport = await fetchLatestUserReport(normalizedUserNo, nextPatientId);
      }

      const nextReportId = nextReport?.report_id ? String(nextReport.report_id) : '';

      setPatients(normalizedPatients);
      setPatientId(nextPatientId);
      setReportId(nextReportId);

      if (!nextPatientId) {
        setScopeError('등록된 가족(patient_id)이 없습니다.');
        return;
      }

      if (!nextReportId) {
        setScopeError('선택한 가족의 최신 non-superseded 리포트가 없습니다.');
      }

      if (autoLoad) {
        await loadIdentityConsole({
          userNo: normalizedUserNo,
          patientId: nextPatientId,
          reportId: nextReportId,
        });
      }
    } catch (loadError) {
      setPatients([]);
      setPatientId('');
      setReportId('');
      setScopeError(loadError?.message || '가족/리포트 범위를 불러오지 못했습니다.');
    } finally {
      setScopeLoading(false);
    }
  };

  const handlePatientChange = async (event) => {
    const nextPatientId = event.target.value;
    setPatientId(nextPatientId);
    setReportId('');
    setScopeError('');
    setError('');
    clearConsoleResults();

    if (!userNo || !nextPatientId) {
      return;
    }

    setScopeLoading(true);
    try {
      const latestReport = await fetchLatestUserReport(userNo, nextPatientId);
      const nextReportId = latestReport?.report_id ? String(latestReport.report_id) : '';
      setReportId(nextReportId);

      if (!nextReportId) {
        setScopeError('선택한 가족의 최신 non-superseded 리포트가 없습니다.');
      }

      await loadIdentityConsole({
        userNo,
        patientId: nextPatientId,
        reportId: nextReportId,
      });
    } catch (loadError) {
      setScopeError(loadError?.message || '선택한 가족의 최신 리포트를 불러오지 못했습니다.');
    } finally {
      setScopeLoading(false);
    }
  };

  useEffect(() => {
    initializePatientScope(defaultUserNo);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const summaryCards = useMemo(
    () => [
      { label: 'timeline items', value: timeline.length },
      { label: 'memory turns', value: memoryTurns.length },
      { label: 'memory hits', value: memorySearch?.returned_count || 0 },
      { label: 'search mode', value: memorySearch?.search_mode || '-' },
    ],
    [timeline, memoryTurns, memorySearch],
  );

  return (
    <div className="identity-console-page">
      <section className="identity-console-hero">
        <div>
          <p className="identity-console-eyebrow">UI-IDENTITY-01</p>
          <h1>Identity Console 확장</h1>
          <p>
            card/events/memory_turns/memory_index/timeline 중 현재 Sprint에서 바로 검증 가능한 타임라인, 메모리 턴,
            메모리 인덱스 검색을 한 화면에 통합합니다.
          </p>
        </div>
      </section>

      <section className="identity-console-layout">
        <aside className="identity-console-sidebar identity-console-card">
          <h2>조회 조건</h2>
          <label>
            로그인 사용자(user_no)
            <input
              value={userNo}
              onChange={(event) => setUserNo(event.target.value)}
            />
          </label>
          <label>
            가족(patient_id)
            <select value={patientId} onChange={handlePatientChange} disabled={scopeLoading || patientOptions.length === 0}>
              <option value="">
                {scopeLoading ? '가족 목록을 불러오는 중입니다.' : '가족을 선택해 주세요.'}
              </option>
              {patientOptions.map((patient) => (
                <option key={patient.id} value={patient.id}>
                  {patient.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            report_id
            <input
              value={reportId}
              onChange={(event) => setReportId(event.target.value)}
              placeholder={scopeLoading ? '최신 리포트 조회 중' : '최신 리포트 없음'}
            />
          </label>
          <p className="identity-console-subtext">
            가족을 변경하면 해당 가족의 최신 non-superseded 리포트를 기준으로 timeline/memory를 다시 조회합니다.
          </p>
          <label>
            memory query
            <input value={query} onChange={(event) => setQuery(event.target.value)} />
          </label>
          <div className="identity-console-actions">
            <button
              type="button"
              onClick={() => loadIdentityConsole({ userNo, patientId, reportId })}
              disabled={loading || scopeLoading || !patientId}
            >
              {loading || scopeLoading ? '조회 중...' : '조회 실행'}
            </button>
          </div>
          {scopeError ? <div className="identity-console-error">{scopeError}</div> : null}
          {error ? <div className="identity-console-error">{error}</div> : null}
        </aside>

        <main className="identity-console-main">
          <section className="identity-console-summary-grid">
            {summaryCards.map((item) => (
              <article key={item.label} className="identity-console-card">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </article>
            ))}
          </section>

          <section className="identity-console-detail-grid">
            <article className="identity-console-card">
              <h2>통합 타임라인</h2>
              <div className="identity-console-list">
                {timeline.length ? (
                  timeline.map((item) => (
                    <article key={item.id} className="identity-console-list-item">
                      <header>
                        <span>{typeLabel(item.type)}</span>
                        <time>{formatDateTime(item.occurred_at)}</time>
                      </header>
                      <h3>{item.title || item.summary || item.id}</h3>
                      <p>{item.summary || '-'}</p>
                    </article>
                  ))
                ) : (
                  <p className="identity-console-empty">조회된 타임라인 항목이 없습니다.</p>
                )}
              </div>
            </article>

            <article className="identity-console-card">
              <h2>Memory Turns</h2>
              <div className="identity-console-list">
                {memoryTurns.length ? (
                  memoryTurns.map((item, index) => (
                    <article key={`${item.dec_id || 'turn'}-${index}`} className="identity-console-list-item">
                      <header>
                        <span>{item.intent || 'unknown'}</span>
                        <time>{formatDateTime(item.occurred_at || item.created_at)}</time>
                      </header>
                      <p><strong>Q.</strong> {item.question || '-'}</p>
                      <p><strong>A.</strong> {item.answer || '-'}</p>
                    </article>
                  ))
                ) : (
                  <p className="identity-console-empty">조회된 memory turn이 없습니다.</p>
                )}
              </div>
            </article>

            <article className="identity-console-card">
              <h2>Memory Index Search</h2>
              <div className="identity-console-chip-row">
                <span className="identity-console-chip">mode: {memorySearch?.search_mode || '-'}</span>
                <span className="identity-console-chip">count: {memorySearch?.returned_count || 0}</span>
              </div>
              <div className="identity-console-list">
                {Array.isArray(memorySearch?.items) && memorySearch.items.length ? (
                  memorySearch.items.map((item) => (
                    <article key={item.id} className="identity-console-list-item">
                      <header>
                        <span>{item.content_type}</span>
                        <time>{formatDateTime(item.created_date)}</time>
                      </header>
                      <p>{item.content_text || '-'}</p>
                      <pre>{JSON.stringify(item.metadata || {}, null, 2)}</pre>
                    </article>
                  ))
                ) : (
                  <p className="identity-console-empty">조회된 memory hit가 없습니다.</p>
                )}
              </div>
            </article>
          </section>
        </main>
      </section>
    </div>
  );
}

export default UserTimelineConsolePage;
